/**
 * The Wrap-up card in place in the conversation that made the plan (a live session's `plan-wrapup` marker, D372): the
 * same five steps, states and actions as the project's Plans, read from the project home — the plan record and its
 * wrap-up (`GET /projects/v1/:id/plans`, `GET /projects/v1/:id/wrap-ups`) — so a step answered here, in Plans, on the
 * desktop or by an agent shows in all of them. Once the plan has no open wrap-up the card folds to one line saying how
 * it closed.
 *
 * Ported from the workbench's webview-ui/src/components/facilitator/PlanWrapUpMessage.tsx (9a76e5e2): the desktop reads
 * the plan live from its board; the console reads the home's records and files an ask or a verdict the way Plans does.
 */

import type { PlanInput } from '@aioe/shared';
import { WRAP_UP } from '@aioe/shared/models';
import { CircleCheckFill, TriangleExclamationFill } from '@gravity-ui/icons';
import { Link } from 'react-router-dom';

import { routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { WrapUpCard } from './WrapUpCard.tsx';
import { wrapUpPlan } from './wrapUpPlan.ts';

const NOTE = 'rounded-xl border border-separator bg-surface/60 px-3 py-2 text-xs text-muted';

const CLOSED_WORDS: Record<string, string> = { verified: 'done and verified', done: 'done', abandoned: 'abandoned', approved: 'approved, not started', dispatched: 'under way', draft: 'a draft' };

export function PlanWrapUpMessage({ projectId, marker }: { projectId?: string; marker: { teamId?: string; planId?: string; rootId?: string } }) {
  const planId = marker.planId;
  const ready = Boolean(projectId && planId);
  const plans = useLoad(() => (ready ? routes.projectPlans(projectId!) : Promise.resolve(undefined)), [projectId, planId], 30_000);
  const wrapUps = useLoad(() => (ready ? routes.wrapUps(projectId!).catch(() => undefined) : Promise.resolve(undefined)), [projectId, planId], 30_000);
  const role = useLoad(() => (ready ? routes.project(projectId!).then((p) => p.role).catch(() => undefined) : Promise.resolve(undefined)), [projectId]);

  if (!ready) return <div className={NOTE} role="note">{WRAP_UP}: open the project’s Plans to see where this plan stands.</div>;
  const rows = plans.data?.plans ?? [];
  const same = <T extends { rootId: string }>(x: T) => !marker.rootId || x.rootId === marker.rootId;
  const p = rows.find((x) => x.id === planId && same(x));
  const plansHref = `/projects/${encodeURIComponent(projectId!)}?tab=work&part=plans`;
  if (!plans.data) return <div className={NOTE} role="note">{plans.error ? `${WRAP_UP}: the plan could not be read. ${plans.error}` : `${WRAP_UP}: reading the plan…`}</div>;
  if (!p) return <div className={NOTE} role="note">{WRAP_UP}: the plan is no longer on its board.</div>;

  const plan = p.data as PlanInput & { outcome?: unknown };
  const w = wrapUps.data?.wrapUps.find((x) => x.planId === p.id && (!p.rootId || x.rootId === p.rootId));
  const canWrite = role.data !== undefined && role.data !== 'viewer';
  const reload = () => {
    plans.reload();
    wrapUps.reload();
  };
  if (w && !plan.unify && p.status !== 'abandoned') {
    return (
      <div data-wrap-up-message>
        <WrapUpCard
          plan={wrapUpPlan(p, plan, w)}
          asks={w.asks}
          canWrite={canWrite}
          onAsk={async (ask) => {
            await routes.planWrapUpAsk(projectId!, p.rootId, p.id, ask);
            reload();
          }}
          onVerdict={async (criterionId, verdict) => {
            await routes.planAcceptance(projectId!, p.rootId, p.id, criterionId, verdict);
            reload();
          }}
        />
      </div>
    );
  }
  const ok = p.status !== 'abandoned';
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-separator bg-surface/60 px-3 py-2 text-xs" role="note" data-wrap-up-closed>
      {ok ? <CircleCheckFill className="size-3.5 shrink-0 text-success" aria-hidden /> : <TriangleExclamationFill className="size-3.5 shrink-0 text-warning" aria-hidden />}
      <span className="min-w-0 flex-1 text-foreground">
        {WRAP_UP} · {plan.ref ?? 'the plan'}: {plan.unify ? 'closed' : (CLOSED_WORDS[p.status] ?? p.status)}.
      </span>
      <Link to={plansHref} className="shrink-0 text-xs text-accent hover:underline">
        Open in Plans
      </Link>
    </div>
  );
}
